// src/components/ScoreTooltips.js
// Centralized score explanation tooltips for transparency
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RiQuestionLine } from 'react-icons/ri';

// Score explanation definitions
export const scoreExplanations = {
    // Urgency scores by entity type
    urgency: {
        Issue: {
            title: 'Issue Urgency',
            description: 'Based on severity level',
            factors: [
                { label: 'High severity', value: '3.0' },
                { label: 'Medium severity', value: '2.0' },
                { label: 'Low severity', value: '1.0' },
            ],
        },
        ActionItem: {
            title: 'Action Item Urgency',
            description: 'Based on deadline proximity',
            factors: [
                { label: 'Overdue', value: '3.0' },
                { label: 'Due within 7 days', value: '2.5' },
                { label: 'Due within 14 days', value: '2.0' },
                { label: 'Open (no deadline)', value: '1.5' },
            ],
        },
        Goal: {
            title: 'Goal Urgency',
            description: 'Based on target date proximity',
            factors: [
                { label: 'Overdue', value: '3.0' },
                { label: 'Due within 30 days', value: '2.0' },
                { label: 'No deadline or >30 days', value: '1.0' },
            ],
        },
        Initiative: {
            title: 'Initiative Urgency',
            description: 'Based on status',
            factors: [
                { label: 'Blocked', value: '2.5' },
                { label: 'Open', value: '1.5' },
                { label: 'Completed', value: '0.5' },
            ],
        },
    },
    
    // Importance score (same for all types)
    importance: {
        title: 'Importance Score',
        description: 'Number of connections in the knowledge graph',
        explanation: 'Items with more relationships (linked goals, initiatives, issues, action items, and communications) are considered more important because they have broader impact.',
    },
    
    // Goal priority score
    goalPriority: {
        title: 'Goal Priority Score',
        description: 'Weighted combination of risk factors',
        factors: [
            { label: 'Blocked initiatives factor', weight: '40%', description: 'More blocking issues = higher priority' },
            { label: 'Negative sentiment', weight: '30%', description: 'Lower sentiment in recent comms = higher priority' },
            { label: 'Neglect factor', weight: '30%', description: 'Declining mentions = higher priority' },
        ],
        note: 'Higher score = needs more attention',
    },
    
    // Health score
    healthScore: {
        title: 'Account Health Score',
        description: 'Calculated from the Framework Score below, which evaluates multiple components of account health.',
        note: 'See the Framework breakdown below for details on each component contributing to this score.',
        interpretation: [
            { range: '80-100%', label: 'Good Health', color: 'var(--pallium-success-green)' },
            { range: '60-79%', label: 'Needs Attention', color: 'var(--pallium-warning-yellow)' },
            { range: '0-59%', label: 'At Risk', color: 'var(--pallium-error-red)' },
        ],
    },
    
    // Initiative momentum
    initiativeMomentum: {
        title: 'Initiative Momentum',
        description: 'Activity and progress indicator',
        factors: [
            { label: 'Recent communications', description: 'Active discussion indicates progress' },
            { label: 'Action item completion', description: 'Tasks being closed' },
            { label: 'Blocking issues', description: 'Fewer blockers = better momentum' },
        ],
    },
    
    // Issue risk
    issueRisk: {
        title: 'Issue Risk Score',
        description: 'Potential impact if unresolved',
        factors: [
            { label: 'Severity', description: 'High/Medium/Low classification' },
            { label: 'Blocked initiatives', description: 'Number of initiatives this blocks' },
            { label: 'Age', description: 'How long issue has been open' },
        ],
    },
    
    // Action item priority
    actionItemPriority: {
        title: 'Action Item Priority',
        description: 'Urgency based on multiple factors',
        factors: [
            { label: 'Initiative blockers', weight: '40%', description: 'Supports blocked initiatives' },
            { label: 'Deadline pressure', weight: '30%', description: 'Proximity to due date' },
            { label: 'Mention spike', weight: '20%', description: 'Increased discussion recently' },
            { label: 'Negative sentiment', weight: '10%', description: 'Concern in communications' },
        ],
    },
};

// Tooltip styles matching Apollo design
const tooltipStyles = {
    container: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
    },
    trigger: {
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'help',
        color: 'var(--pallium-icon-gray)',
        marginLeft: '4px',
    },
    popup: {
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '280px',
        padding: '12px',
        backgroundColor: 'var(--pallium-primary-dark-navy)',
        color: 'var(--pallium-white)',
        borderRadius: 'var(--pallium-border-radius-lg, 8px)',
        boxShadow: 'var(--pallium-shadow-lg)',
        zIndex: 'var(--pallium-z-dropdown, 1000)',
        fontSize: '12px',
        lineHeight: '1.4',
    },
    arrow: {
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid var(--pallium-primary-dark-navy)',
    },
    title: {
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '6px',
        color: 'var(--pallium-white)',
    },
    description: {
        color: 'rgba(255, 255, 255, 0.8)',
        marginBottom: '8px',
    },
    factorList: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    factor: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
    factorLabel: {
        color: 'rgba(255, 255, 255, 0.9)',
    },
    factorValue: {
        fontWeight: 600,
        color: 'var(--pallium-primary-purple-light, #a78bfa)',
        fontFamily: 'monospace',
    },
    factorWeight: {
        fontSize: '10px',
        color: 'rgba(255, 255, 255, 0.6)',
        marginLeft: '4px',
    },
    note: {
        marginTop: '8px',
        padding: '6px 8px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '4px',
        fontSize: '11px',
        color: 'rgba(255, 255, 255, 0.8)',
    },
};

// Tooltip Portal - renders tooltip at document body level to avoid clipping
const TooltipPortal = ({ children, triggerRef, isVisible }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    
    useEffect(() => {
        if (isVisible && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const tooltipWidth = 280;
            
            // Use clientWidth to get actual viewport width excluding scrollbar
            const viewportWidth = document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight;
            
            // Position below the trigger, centered
            let left = rect.left + rect.width / 2 - tooltipWidth / 2;
            let top = rect.bottom + 8;
            
            // Keep within viewport bounds with 12px padding
            if (left < 12) left = 12;
            if (left + tooltipWidth > viewportWidth - 12) {
                left = viewportWidth - tooltipWidth - 12;
            }
            
            // If tooltip would go off bottom, show above instead
            if (top + 200 > viewportHeight) {
                top = rect.top - 8; // Will be adjusted by transform
            }
            
            setPosition({ top, left });
        }
    }, [isVisible, triggerRef]);
    
    if (!isVisible) return null;
    
    return ReactDOM.createPortal(
        <div style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 9999,
        }}>
            {children}
        </div>,
        document.body
    );
};

// Reusable Score Tooltip Component
export const ScoreTooltip = ({ type, entityType, size = 'sm' }) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerRef = useRef(null);
    
    // Get the appropriate explanation
    let explanation;
    if (type === 'urgency' && entityType) {
        explanation = scoreExplanations.urgency[entityType];
    } else {
        explanation = scoreExplanations[type];
    }
    
    if (!explanation) return null;
    
    const iconSize = size === 'sm' ? 12 : size === 'md' ? 14 : 16;
    
    const popupStyle = {
        width: '280px',
        padding: '12px',
        backgroundColor: 'var(--pallium-primary-dark-navy)',
        color: 'var(--pallium-white)',
        borderRadius: 'var(--pallium-border-radius-lg, 8px)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        fontSize: '12px',
        lineHeight: '1.4',
    };
    
    return (
        <span 
            ref={triggerRef}
            style={tooltipStyles.container}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            <span style={tooltipStyles.trigger}>
                <RiQuestionLine style={{ width: iconSize, height: iconSize }} />
            </span>
            
            <TooltipPortal triggerRef={triggerRef} isVisible={isVisible}>
                <div style={popupStyle}>
                    <div style={tooltipStyles.title}>{explanation.title}</div>
                    <div style={tooltipStyles.description}>{explanation.description}</div>
                    
                    {explanation.factors && (
                        <ul style={tooltipStyles.factorList}>
                            {explanation.factors.map((factor, idx) => (
                                <li key={idx} style={tooltipStyles.factor}>
                                    <span style={tooltipStyles.factorLabel}>
                                        {factor.label}
                                        {factor.weight && (
                                            <span style={tooltipStyles.factorWeight}>({factor.weight})</span>
                                        )}
                                    </span>
                                    {factor.value && (
                                        <span style={tooltipStyles.factorValue}>{factor.value}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    
                    {explanation.explanation && (
                        <div style={tooltipStyles.note}>{explanation.explanation}</div>
                    )}
                    
                    {explanation.note && (
                        <div style={tooltipStyles.note}>{explanation.note}</div>
                    )}
                    
                    {explanation.interpretation && (
                        <div style={{ marginTop: '8px' }}>
                            {explanation.interpretation.map((item, idx) => (
                                <div key={idx} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    fontSize: '11px',
                                    marginBottom: '2px',
                                }}>
                                    <span style={{ 
                                        width: '8px', 
                                        height: '8px', 
                                        borderRadius: '50%', 
                                        backgroundColor: item.color,
                                    }} />
                                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{item.range}:</span>
                                    <span style={{ color: item.color }}>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </TooltipPortal>
        </span>
    );
};

// Inline score display with tooltip
export const ScoreWithTooltip = ({ 
    label, 
    value, 
    type, 
    entityType, 
    color,
    size = 'sm',
}) => {
    const textSize = size === 'sm' ? '10px' : size === 'md' ? '11px' : '12px';
    
    return (
        <span style={{ 
            display: 'inline-flex', 
            alignItems: 'center',
            fontSize: textSize,
            color: color || 'var(--pallium-text-secondary)',
        }}>
            {label && <span style={{ marginRight: '2px' }}>{label}:</span>}
            <span style={{ fontWeight: 500, color: color || 'var(--pallium-text-primary)' }}>
                {typeof value === 'number' ? value.toFixed(1) : value}
            </span>
            <ScoreTooltip type={type} entityType={entityType} size={size} />
        </span>
    );
};

export default ScoreTooltip;

